import {
  EmptyState,
  MetricBlock,
  StatusPill,
  HealthBar,
  type StatusLevel,
} from '@/lib';
import type { ApiClients } from '@/api/client';
import { useFleetOverview } from './queries';

export type S8ReportsProps = { clients: ApiClients };

const SEVERITY_TONE: Record<string, StatusLevel> = {
  critical: 'critical',
  warning: 'warn',
  warn: 'warn',
  watch: 'watch',
  info: 'info',
};

/**
 * S8 Reports — fleet-level KPIs and risk distribution. Composes the
 * existing /v1/reporting/fleet-overview aggregate:
 *   - hero metrics (total assets, critical alerts, avg health)
 *   - top-N risk assets table
 *   - recent alerts strip
 *   - risk matrix (criticality × severity) heatmap
 *
 * Pure read view — no mutations, no role gating. Anyone with
 * dashboard.view sees the report.
 */
export function S8Reports({ clients }: S8ReportsProps) {
  const overview = useFleetOverview(clients);

  if (overview.isError) {
    return (
      <EmptyState
        variant="error"
        title="Couldn't load fleet overview"
        body={String(overview.error)}
        cta={{ label: 'Retry', onClick: () => overview.refetch() }}
      />
    );
  }
  if (overview.isLoading || !overview.data) {
    return <p className="text-fs-body text-gray-700">Loading reports…</p>;
  }

  const d = overview.data;
  // The reporting aggregate may omit array fields (e.g. alerts comes back null
  // when no alert snapshot exists). Default them so the render never hits
  // `.length`/`.map`/`.slice` on null.
  const topAssets = d.topAssets ?? [];
  const alerts = d.alerts ?? [];
  const riskMatrix = d.riskMatrix ?? [];
  const xLabels =
    d.riskMatrixXAxisLabels && d.riskMatrixXAxisLabels.length > 0
      ? d.riskMatrixXAxisLabels
      : ['Critical', 'High', 'Medium', 'Low'];

  return (
    <div className="flex flex-col gap-sp-6">
      <header>
        <p className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
          Fleet reports
        </p>
        <h1 className="mt-sp-1 text-fs-h1 font-medium text-gray-900">
          Health &amp; alert overview
        </h1>
      </header>

      <section
        aria-label="Top-level KPIs"
        className="grid grid-cols-1 gap-sp-3 sm:grid-cols-3"
      >
        <MetricBlock label="Total assets" value={d.totalAssets.toLocaleString()} />
        <MetricBlock
          label="Critical alerts"
          value={d.criticalAlerts.toLocaleString()}
          tone={d.criticalAlerts > 0 ? 'critical' : undefined}
        />
        <MetricBlock
          label="Avg health"
          value={`${d.avgHealth.toFixed(0)}/100`}
          tone={d.avgHealth < 60 ? 'critical' : 'neutral'}
        />
      </section>

      <section aria-labelledby="risk-matrix-heading">
        <h2
          id="risk-matrix-heading"
          className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700"
        >
          Risk matrix
        </h2>
        <RiskMatrix grid={riskMatrix} xLabels={xLabels} />
      </section>

      <section aria-labelledby="top-assets-heading">
        <h2
          id="top-assets-heading"
          className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700"
        >
          Top risk assets
        </h2>
        {topAssets.length === 0 ? (
          <p className="mt-sp-3 text-fs-body text-gray-700">
            No assets currently flagged.
          </p>
        ) : (
          <div className="mt-sp-3 overflow-x-auto rounded-r-card border border-gray-300 bg-gray-50">
            <table className="min-w-full text-fs-meta">
              <thead>
                <tr className="text-gray-700">
                  <th className="px-sp-3 py-sp-2 text-left font-medium uppercase tracking-[0.14em]">
                    Asset
                  </th>
                  <th className="px-sp-3 py-sp-2 text-left font-medium uppercase tracking-[0.14em]">
                    Site
                  </th>
                  <th className="px-sp-3 py-sp-2 text-left font-medium uppercase tracking-[0.14em]">
                    Criticality
                  </th>
                  <th className="px-sp-3 py-sp-2 text-right font-medium uppercase tracking-[0.14em]">
                    Health
                  </th>
                  <th className="px-sp-3 py-sp-2 text-right font-medium uppercase tracking-[0.14em]">
                    Open alerts
                  </th>
                </tr>
              </thead>
              <tbody>
                {topAssets.map((a) => (
                  <tr key={a.assetId} className="border-t border-gray-300">
                    <td className="px-sp-3 py-sp-2 text-gray-900">{a.name}</td>
                    <td className="px-sp-3 py-sp-2 text-gray-700">{a.siteName}</td>
                    <td className="px-sp-3 py-sp-2 text-gray-700">{a.criticality}</td>
                    <td className="px-sp-3 py-sp-2 text-right">
                      <div className="flex items-center justify-end gap-sp-2">
                        <span className="font-mono text-gray-900">
                          {a.currentHealthScore.toFixed(0)}
                        </span>
                        <HealthBar value={a.currentHealthScore} size="sub" thresholds={{ warn: 70, critical: 50 }} />
                      </div>
                    </td>
                    <td className="px-sp-3 py-sp-2 text-right font-mono text-gray-900">
                      {a.openAlertCount}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section aria-labelledby="recent-alerts-heading">
        <h2
          id="recent-alerts-heading"
          className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700"
        >
          Recent alerts
        </h2>
        {alerts.length === 0 ? (
          <p className="mt-sp-3 text-fs-body text-gray-700">No alerts in window.</p>
        ) : (
          <ul className="mt-sp-3 flex flex-col gap-sp-2">
            {alerts.slice(0, 8).map((a) => (
              <li
                key={a.alertId}
                className="grid grid-cols-[1fr_auto] items-center gap-sp-3 rounded-r-default border border-gray-300 bg-gray-50 px-sp-3 py-sp-2"
              >
                <div className="min-w-0">
                  <p className="truncate text-fs-body font-medium text-gray-900">
                    {a.title}
                  </p>
                  <p className="truncate text-fs-meta text-gray-700">
                    {a.assetName} · {new Date(a.createdAt).toLocaleString()}
                  </p>
                </div>
                <StatusPill
                  level={SEVERITY_TONE[a.severity.toLowerCase()] ?? 'watch'}
                  label={a.severity.toUpperCase()}
                />
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function RiskMatrix({
  grid,
  xLabels,
}: {
  grid: number[][];
  xLabels: string[];
}) {
  if (grid.length === 0 || grid[0].length === 0) {
    return (
      <p className="mt-sp-3 text-fs-body text-gray-700">No risk distribution data.</p>
    );
  }
  let peak = 0;
  for (const row of grid) {
    for (const v of row) {
      if (v > peak) peak = v;
    }
  }
  if (peak === 0) peak = 1;
  // Rows = severity (top→bottom: critical/warn/watch/healthy)
  const yLabels = ['Critical', 'Warn', 'Watch', 'Healthy'].slice(0, grid.length);
  return (
    <div className="mt-sp-3 overflow-x-auto rounded-r-card border border-gray-300 bg-gray-50">
      <table className="min-w-full text-fs-meta">
        <thead>
          <tr className="text-gray-700">
            <th className="px-sp-3 py-sp-2" />
            {xLabels.map((x) => (
              <th
                key={x}
                className="px-sp-3 py-sp-2 text-center font-medium uppercase tracking-[0.14em]"
              >
                {x}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {grid.map((row, ri) => (
            <tr key={ri} className="border-t border-gray-300">
              <th
                scope="row"
                className="px-sp-3 py-sp-2 text-left font-medium text-gray-900 uppercase tracking-[0.14em]"
              >
                {yLabels[ri] ?? `Row ${ri}`}
              </th>
              {row.map((v, ci) => {
                const opacity = v / peak;
                return (
                  <td
                    key={ci}
                    className="px-sp-3 py-sp-2 text-center font-mono text-gray-900"
                    style={{ background: `rgba(220,80,80,${opacity})` }}
                    aria-label={`${yLabels[ri] ?? 'Row'} × ${xLabels[ci] ?? 'col'}: ${v} assets`}
                  >
                    {v}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
